/** Pure helpers for the Pro Setup Flow's "Who's in your crew?" card (issue #360).
 *
 *  WHY THIS CARD EXISTS: Kids Crew Setup at signup is skippable
 *  (kin_kids_crew_skipped). A mom who skipped it and later buys Pro used to get
 *  no kids card at all in the celebration modal, because that modal's kid
 *  birthdays card is conditional on kids ALREADY existing. Kin then never
 *  learned her kids' names or birthdays.
 *
 *  WHERE KID NAMES LIVE (verified, do not guess): the roster is
 *  user_preferences.known_kid_names (text[]) plus user_preferences
 *  .known_kid_colors (jsonb, name -> hex). NOT a kids table, NOT family_context
 *  (family_context only holds facts ABOUT a kid, e.g. key='birthdate'). This
 *  module produces exactly the shape KidsCrewSetup.tsx and MyKidsSection.tsx
 *  write, so downstream surfaces (Tasks tab entity resolution, chat context,
 *  birthday guard, Family Profile) behave identically regardless of which path
 *  created the kid.
 */

import { findKidColor, pickAvailableKidColor, type KidColorRecord } from "@/lib/kidColors";

/** Trim a kid's name, preserving the exact casing the user typed. Same rule as
 *  cleanName in KidsCrewSetup.tsx and MyKidsSection.tsx. */
export function cleanKidName(raw: string): string {
  return String(raw || "").trim();
}

export interface MergedKidNames {
  /** Full roster to persist: existing names first, then newly added ones. */
  names: string[];
  /** Full color map to persist. Existing assignments are never reshuffled. */
  colors: KidColorRecord;
  /** Only the names this card actually added (drives birthday cards + receipt). */
  added: string[];
}

/** Merge freshly typed names into the existing roster.
 *
 *  Dedup is case-insensitive both against the existing roster and within the
 *  typed list itself (first occurrence wins, exact casing preserved). A name
 *  that matches an existing kid is never duplicated, and never recolored.
 */
export function mergeKidNames(
  existingNames: string[],
  existingColors: KidColorRecord,
  typedNames: string[]
): MergedKidNames {
  const names = (Array.isArray(existingNames) ? existingNames : []).filter(
    (n) => typeof n === "string" && n.trim().length > 0
  );
  const colors: KidColorRecord =
    existingColors && typeof existingColors === "object" && !Array.isArray(existingColors)
      ? { ...existingColors }
      : {};

  const seen = new Set(names.map((n) => n.toLowerCase()));
  const added: string[] = [];

  for (const raw of typedNames) {
    const name = cleanKidName(raw);
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    names.push(name);
    added.push(name);
  }

  // Assign a stable color to every kid missing one (defensive for names that
  // predate colors), using the same first-unused-in-palette rule as Settings.
  for (const name of names) {
    if (!findKidColor(name, colors)) {
      colors[name] = pickAvailableKidColor(colors);
    }
  }

  return { names, colors, added };
}

/** "Sarah" / "Sarah and Carter" / "Sarah, Carter, and Blake". */
export function formatKidNameList(names: string[]): string {
  if (names.length === 0) return "";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
}

/** Close-card receipt line (spec §B.14 frame: leading ✓, no em dashes).
 *  Names what was saved AND where she can change it later. */
export function kidNamesReceiptLine(names: string[]): string {
  return `✓ ${formatKidNameList(names)} added to your crew. Edit anytime in Family Profile`;
}
